import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Select } from 'antd';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionAction } from '@hooks/useSessionAction';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { type ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';

import { SessionFormItem } from '@components/Clinicians/Session/Components/SessionFormItem';

export const SpecialityFormItem = ({
  actionCode,
  disabled = false,
  onChange
}: {
  actionCode: ApptActionCodes;
  disabled?: boolean;
  onChange?: (value: string) => void;
}) => {
  const { t } = useTranslation('common');
  const appointment_id = useSessionAppointmentId();
  const {
    appointment: { appointment_completed: isCompleted, read_only: readonly }
  } = useSessionAppointment();

  const { meta, SPECIALITY_KEY, SUBSPECIALITY_KEY, selectedSubType } = useSessionAction(actionCode);

  const { detailsCurrentAppointment } = useAppointmentDetails(appointment_id);
  const { action_spec_cat_code } =
    detailsCurrentAppointment?.appt_actions.find(({ appt_action_code }) => appt_action_code === actionCode) || {};

  const [speciality, setSpeciality] = useSessionFormState(SPECIALITY_KEY, action_spec_cat_code || undefined);
  const [, setSubSpeciality] = useSessionFormState(SUBSPECIALITY_KEY);

  const specialitiesMeta = useMemo(() => {
    if (!meta?.action_subtypes) return [];

    const allSpecs = new Map();

    meta.action_subtypes.forEach((subtype) => {
      subtype.action_spec_cat_codes?.forEach((code, idx) => {
        if (!allSpecs.has(code)) {
          allSpecs.set(code, {
            cat_name: subtype.action_spec_cat_descs?.[idx],
            cat_code: code
          });
        }
      });
    });

    return Array.from(allSpecs.values());
  }, [meta?.action_subtypes]);

  return (
    <SessionFormItem name={SPECIALITY_KEY} label={t('What specialty are you referring to?')} required>
      <Select
        showSearch
        disabled={isCompleted || readonly || !selectedSubType || disabled}
        data-testid="speciality-select"
        optionFilterProp="label"
        value={speciality}
        onSelect={(value) => {
          setSpeciality(value);
          setSubSpeciality(undefined);

          onChange?.(value);
        }}
        options={specialitiesMeta?.map((res) => ({
          label: res.cat_name,
          value: res.cat_code
        }))}
      />
    </SessionFormItem>
  );
};
